/**
 * Settings page — ledger description prefixes, PDF config, variance
 * thresholds.
 *
 * All settings round-trip through `/api/settings/...` endpoints. Changes
 * take effect on the next operation (no restart required for any of these
 * values).
 */
import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import { AppShell } from '../ui/AppShell';
import { useReadOnly } from '../ui/AppShell';
import { PageToolbar } from '../ui/PageToolbar';
import { Card } from '../ui/components';
import {
  Button,
  ErrorBanner,
  Spinner,
  TextInput,
} from '../ui/primitives';
import { weekLabelForDebug } from '../lib/week';

export function SettingsPage() {
  const readOnly = useReadOnly();
  return (
    <AppShell
      title="Settings"
      toolbar={<PageToolbar title="Settings" />}
    >
      {readOnly && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-small text-amber-700">
          You are in read-only mode. Settings cannot be changed.
        </div>
      )}
      <div className="grid gap-4 lg:grid-cols-2">
        <LedgerDescriptionCard readOnly={readOnly} />
        <PdfConfigCard readOnly={readOnly} />
        <VarianceThresholdsCard readOnly={readOnly} />
        <NetworkBindCard readOnly={readOnly} />
      </div>
    </AppShell>
  );
}

// ── Ledger description prefix ────────────────────────────────────────────────

type KVSetting<T> = { key: string; defaultValue: T };
async function getRawSetting<T>(s: KVSetting<T>): Promise<T> {
  void s;
  throw new Error('unused');
}
void getRawSetting;

function LedgerDescriptionCard({ readOnly }: { readOnly: boolean }) {
  const qc = useQueryClient();
  const [prefix, setPrefix] = useState('');
  const [paymentPrefix, setPaymentPrefix] = useState('');
  const [error, setError] = useState<string | null>(null);

  const settings = useQuery<{ prefix: string; paymentPrefix: string }>({
    queryKey: ['settings', 'ledger'],
    queryFn: () => api.get('/api/settings/ledger'),
  });
  useEffect(() => {
    if (settings.data) {
      setPrefix(settings.data.prefix);
      setPaymentPrefix(settings.data.paymentPrefix);
    }
  }, [settings.data]);

  const saveMut = useMutation({
    mutationFn: async () => {
      await api.put('/api/settings/ledger', { prefix, paymentPrefix });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['settings', 'ledger'] }),
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  const sampleWeek = weekLabelForDebug('2026-04-06');
  const sampleDesc = `${prefix}LOTUS365DA ${sampleWeek}`;
  const samplePaymentDesc = paymentPrefix
    .replaceAll('{payer}', 'SRB')
    .concat('C0F999 VICKY');

  return (
    <Card header={{ title: 'Ledger description prefixes' }}>
      {settings.isLoading ? (
        <div className="flex items-center gap-2 text-caption text-ink-muted">
          <Spinner /> loading…
        </div>
      ) : (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (readOnly) return;
            setError(null);
            saveMut.mutate();
          }}
        >
          <TextInput
            label="Weekly P&L prefix"
            value={prefix}
            onChange={(e) => setPrefix(e.target.value)}
            hint={`Preview: ${sampleDesc}`}
            readOnly={readOnly}
          />
          <TextInput
            label="Payment description template"
            value={paymentPrefix}
            onChange={(e) => setPaymentPrefix(e.target.value)}
            hint={`Preview: ${samplePaymentDesc}`}
            readOnly={readOnly}
          />
          {error && <ErrorBanner>{error}</ErrorBanner>}
          {!readOnly && (
            <div>
              <Button type="submit" disabled={saveMut.isPending}>
                {saveMut.isPending ? <Spinner /> : 'Save'}
              </Button>
            </div>
          )}
        </form>
      )}
    </Card>
  );
}

// ── PDF config ───────────────────────────────────────────────────────────────

function PdfConfigCard({ readOnly }: { readOnly: boolean }) {
  const qc = useQueryClient();
  const settings = useQuery<{ rowsPerPage: number; deviceScaleFactor: number }>({
    queryKey: ['settings', 'pdf'],
    queryFn: () => api.get('/api/settings/pdf'),
  });
  const [rowsPerPage, setRowsPerPage] = useState(40);
  const [deviceScaleFactor, setDeviceScaleFactor] = useState(2);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (settings.data) {
      setRowsPerPage(settings.data.rowsPerPage);
      setDeviceScaleFactor(settings.data.deviceScaleFactor);
    }
  }, [settings.data]);

  const saveMut = useMutation({
    mutationFn: async () =>
      api.put('/api/settings/pdf', { rowsPerPage, deviceScaleFactor }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['settings', 'pdf'] }),
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  return (
    <Card header={{ title: 'PDF / PNG export' }}>
      {settings.isLoading ? (
        <div className="flex items-center gap-2 text-caption text-ink-muted">
          <Spinner /> loading…
        </div>
      ) : (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (readOnly) return;
            setError(null);
            saveMut.mutate();
          }}
        >
          <TextInput
            label="Rows per page"
            type="number"
            min={5}
            max={200}
            value={String(rowsPerPage)}
            onChange={(e) => setRowsPerPage(Math.max(5, Math.min(200, Number(e.target.value) || 40)))}
            hint="Default 40. Range 5–200."
            readOnly={readOnly}
          />
          <TextInput
            label="Device scale factor (PNG)"
            type="number"
            min={1}
            max={4}
            step={0.5}
            value={String(deviceScaleFactor)}
            onChange={(e) => setDeviceScaleFactor(Math.max(1, Math.min(4, Number(e.target.value) || 2)))}
            hint="Default 2. Higher = sharper / larger file."
            readOnly={readOnly}
          />
          {error && <ErrorBanner>{error}</ErrorBanner>}
          {!readOnly && (
            <div>
              <Button type="submit" disabled={saveMut.isPending}>
                {saveMut.isPending ? <Spinner /> : 'Save'}
              </Button>
            </div>
          )}
        </form>
      )}
    </Card>
  );
}

// ── Variance thresholds ──────────────────────────────────────────────────────

function VarianceThresholdsCard({ readOnly }: { readOnly: boolean }) {
  const qc = useQueryClient();
  const settings = useQuery<{ minor: string; review: string }>({
    queryKey: ['settings', 'variance'],
    queryFn: () => api.get('/api/settings/variance'),
  });
  const [minor, setMinor] = useState('');
  const [review, setReview] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (settings.data) {
      setMinor(settings.data.minor);
      setReview(settings.data.review);
    }
  }, [settings.data]);

  const saveMut = useMutation({
    mutationFn: () => api.put('/api/settings/variance', { minor, review }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['settings', 'variance'] }),
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  return (
    <Card header={{ title: 'Variance thresholds (K-INR)' }}>
      <p className="mb-3 text-caption text-ink-muted">
        Applied during settlement compute to classify the delta between
        upline&apos;s stated amount and the app&apos;s recomputed amount.
      </p>
      {settings.isLoading ? (
        <div className="flex items-center gap-2 text-caption text-ink-muted">
          <Spinner /> loading…
        </div>
      ) : (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (readOnly) return;
            setError(null);
            saveMut.mutate();
          }}
        >
          <TextInput
            label="MINOR ≤"
            value={minor}
            onChange={(e) => setMinor(e.target.value)}
            hint="Below this is cosmetic rounding (default 0.001)."
            readOnly={readOnly}
          />
          <TextInput
            label="REVIEW ≤"
            value={review}
            onChange={(e) => setReview(e.target.value)}
            hint="Above MINOR, below this is a review alert (default 1)."
            readOnly={readOnly}
          />
          {error && <ErrorBanner>{error}</ErrorBanner>}
          {!readOnly && (
            <div>
              <Button type="submit" disabled={saveMut.isPending}>
                {saveMut.isPending ? <Spinner /> : 'Save'}
              </Button>
            </div>
          )}
        </form>
      )}
    </Card>
  );
}

// ── Network bind ─────────────────────────────────────────────────────────────

function NetworkBindCard({ readOnly }: { readOnly: boolean }) {
  const qc = useQueryClient();
  const settings = useQuery<{ bind: string; port: number }>({
    queryKey: ['settings', 'network'],
    queryFn: () => api.get('/api/settings/network'),
  });
  const [bind, setBind] = useState('');
  const [port, setPort] = useState(4400);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (settings.data) {
      setBind(settings.data.bind);
      setPort(settings.data.port);
    }
  }, [settings.data]);

  const saveMut = useMutation({
    mutationFn: () => api.put('/api/settings/network', { bind, port }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['settings', 'network'] }),
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  return (
    <Card header={{ title: 'Network bind' }}>
      <p className="mb-3 text-caption text-warn">
        Default is <code className="font-mono text-caption">127.0.0.1</code> (loopback only).
        Changing this exposes the app on your LAN / public internet and requires a
        restart to take effect.
      </p>
      {settings.isLoading ? (
        <div className="flex items-center gap-2 text-caption text-ink-muted">
          <Spinner /> loading…
        </div>
      ) : (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (readOnly) return;
            setError(null);
            saveMut.mutate();
          }}
        >
          <TextInput
            label="Bind"
            value={bind}
            onChange={(e) => setBind(e.target.value)}
            readOnly={readOnly}
          />
          <TextInput
            label="Port"
            type="number"
            value={String(port)}
            onChange={(e) => setPort(Math.max(1, Math.min(65535, Number(e.target.value) || 4400)))}
            readOnly={readOnly}
          />
          {error && <ErrorBanner>{error}</ErrorBanner>}
          {!readOnly && (
            <div>
              <Button type="submit" disabled={saveMut.isPending}>
                {saveMut.isPending ? <Spinner /> : 'Save (restart required)'}
              </Button>
            </div>
          )}
        </form>
      )}
    </Card>
  );
}
